import React, { useState, useEffect } from 'react';
import { FaCog, FaBell, FaSave, FaServer, FaGlobe, FaShieldAlt, FaDatabase } from 'react-icons/fa';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Component for system administrators to configure global settings
 */
const AdminSettings = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      pushNotifications: true,
      systemAlerts: true,
      marketingEmails: false
    },
    security: {
      twoFactorAuth: false,
      passwordExpiry: 90, // days
      loginAttempts: 5,
      sessionTimeout: 30 // minutes
    },
    system: {
      maintenanceMode: false,
      debugMode: false,
      cacheLifetime: 60, // minutes
      defaultTimezone: 'Africa/Nairobi'
    },
    localization: {
      language: 'en',
      dateFormat: 'DD/MM/YYYY',
      timeFormat: '24h',
      currency: 'KES'
    }
  });
  
  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, []);
  
  // Fetch settings from API
  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings');
      
      if (response.data.success && response.data.data) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      showToast('Failed to load settings', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle notification settings changes
  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    
    setSettings({
      ...settings,
      notifications: {
        ...settings.notifications,
        [name]: checked
      }
    });
  };
  
  // Handle security settings changes
  const handleSecurityChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setSettings({
      ...settings,
      security: {
        ...settings.security,
        [name]: type === 'checkbox' ? checked : parseInt(value, 10)
      }
    });
  };
  
  // Handle system settings changes
  const handleSystemChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setSettings({
      ...settings,
      system: {
        ...settings.system,
        [name]: type === 'checkbox' ? checked : value
      }
    });
  };
  
  // Handle localization settings changes
  const handleLocalizationChange = (e) => {
    const { name, value } = e.target;
    
    setSettings({
      ...settings,
      localization: {
        ...settings.localization,
        [name]: value
      }
    });
  };
  
  // Save settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await api.put('/admin/settings', settings);
      
      if (response.data.success) {
        showToast('Settings saved successfully', 'success');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <FaCog className="mr-2 text-primary" /> System Settings
      </h1>
      
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-6 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Notification Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaBell className="mr-2 text-primary" /> Notification Settings
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Email Notifications</h3>
                  <p className="text-sm text-gray-500">
                    Receive system notifications via email
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="email"
                    className="sr-only peer"
                    checked={settings.notifications.email}
                    onChange={handleNotificationChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Push Notifications</h3>
                  <p className="text-sm text-gray-500">
                    Receive push notifications in the browser
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="pushNotifications"
                    className="sr-only peer"
                    checked={settings.notifications.pushNotifications}
                    onChange={handleNotificationChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">System Alerts</h3>
                  <p className="text-sm text-gray-500">
                    Get critical system notifications and alerts
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="systemAlerts"
                    className="sr-only peer"
                    checked={settings.notifications.systemAlerts}
                    onChange={handleNotificationChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Marketing Emails</h3>
                  <p className="text-sm text-gray-500">
                    Receive updates about new features and promotions
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="marketingEmails"
                    className="sr-only peer"
                    checked={settings.notifications.marketingEmails}
                    onChange={handleNotificationChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
          </div>
          
          {/* Security Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaShieldAlt className="mr-2 text-primary" /> Security Settings
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Two-Factor Authentication</h3>
                  <p className="text-sm text-gray-500">
                    Require two-factor authentication for admin users
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="twoFactorAuth"
                    className="sr-only peer"
                    checked={settings.security.twoFactorAuth}
                    onChange={handleSecurityChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div>
                <label htmlFor="passwordExpiry" className="block text-sm font-medium text-gray-700 mb-1">
                  Password Expiry (days)
                </label>
                <input
                  type="number"
                  id="passwordExpiry"
                  name="passwordExpiry"
                  min="0"
                  max="365"
                  value={settings.security.passwordExpiry}
                  onChange={handleSecurityChange}
                  className="form-input w-full max-w-xs"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Number of days before passwords expire (0 = never)
                </p>
              </div>
              
              <div>
                <label htmlFor="loginAttempts" className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Login Attempts
                </label>
                <input
                  type="number"
                  id="loginAttempts"
                  name="loginAttempts"
                  min="1"
                  max="10"
                  value={settings.security.loginAttempts}
                  onChange={handleSecurityChange}
                  className="form-input w-full max-w-xs"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Number of failed login attempts before account is locked
                </p>
              </div>
              
              <div>
                <label htmlFor="sessionTimeout" className="block text-sm font-medium text-gray-700 mb-1">
                  Session Timeout (minutes)
                </label>
                <input
                  type="number"
                  id="sessionTimeout"
                  name="sessionTimeout"
                  min="5"
                  max="480"
                  value={settings.security.sessionTimeout}
                  onChange={handleSecurityChange}
                  className="form-input w-full max-w-xs"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Time of inactivity before user is logged out
                </p>
              </div>
            </div>
          </div>
          
          {/* System Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaServer className="mr-2 text-primary" /> System Settings
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Maintenance Mode</h3>
                  <p className="text-sm text-gray-500">
                    Put the system in maintenance mode (only admins can access)
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="maintenanceMode"
                    className="sr-only peer"
                    checked={settings.system.maintenanceMode}
                    onChange={handleSystemChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Debug Mode</h3>
                  <p className="text-sm text-gray-500">
                    Enable detailed error messages and logging
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="debugMode"
                    className="sr-only peer"
                    checked={settings.system.debugMode}
                    onChange={handleSystemChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
              
              <div>
                <label htmlFor="cacheLifetime" className="block text-sm font-medium text-gray-700 mb-1">
                  Cache Lifetime (minutes)
                </label>
                <input
                  type="number"
                  id="cacheLifetime"
                  name="cacheLifetime"
                  min="5"
                  max="1440"
                  value={settings.system.cacheLifetime}
                  onChange={handleSystemChange}
                  className="form-input w-full max-w-xs"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Time before system cache is refreshed
                </p>
              </div>
              
              <div>
                <label htmlFor="defaultTimezone" className="block text-sm font-medium text-gray-700 mb-1">
                  Default System Timezone
                </label>
                <select
                  id="defaultTimezone"
                  name="defaultTimezone"
                  value={settings.system.defaultTimezone}
                  onChange={handleSystemChange}
                  className="form-select w-full max-w-xs"
                >
                  <option value="Africa/Nairobi">East Africa Time (EAT)</option>
                  <option value="Africa/Lagos">West Africa Time (WAT)</option>
                  <option value="Africa/Cairo">Eastern European Time (EET)</option>
                  <option value="UTC">Coordinated Universal Time (UTC)</option>
                  <option value="Europe/London">Greenwich Mean Time (GMT)</option>
                  <option value="America/New_York">Eastern Time (ET)</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Localization Settings */}
          <div className="bg-white rounded-lg shadow-soft p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <FaGlobe className="mr-2 text-primary" /> Localization Settings
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label htmlFor="language" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Language
                </label>
                <select
                  id="language"
                  name="language"
                  value={settings.localization.language}
                  onChange={handleLocalizationChange}
                  className="form-select w-full"
                >
                  <option value="en">English</option>
                  <option value="sw">Swahili</option>
                  <option value="fr">French</option>
                  <option value="ar">Arabic</option>
                </select>
              </div>
              <div>
                <label htmlFor="dateFormat" className="block text-sm font-medium text-gray-700 mb-1">
                  Date Format
                </label>
                <select
                  id="dateFormat"
                  name="dateFormat"
                  value={settings.localization.dateFormat}
                  onChange={handleLocalizationChange}
                  className="form-select w-full"
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>
              <div>
                <label htmlFor="timeFormat" className="block text-sm font-medium text-gray-700 mb-1">
                  Time Format
                </label>
                <select
                  id="timeFormat"
                  name="timeFormat"
                  value={settings.localization.timeFormat}
                  onChange={handleLocalizationChange}
                  className="form-select w-full"
                >
                  <option value="12h">12-hour (AM/PM)</option>
                  <option value="24h">24-hour</option>
                </select>
              </div>
              <div>
                <label htmlFor="currency" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Currency
                </label>
                <select
                  id="currency"
                  name="currency"
                  value={settings.localization.currency}
                  onChange={handleLocalizationChange}
                  className="form-select w-full"
                >
                  <option value="KES">Kenyan Shilling (KES)</option>
                  <option value="USD">US Dollar (USD)</option>
                  <option value="GBP">British Pound (GBP)</option>
                  <option value="EUR">Euro (EUR)</option>
                </select>
              </div>
            </div>
          </div>
          
          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSaveSettings}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              {saving ? <LoadingSpinner size="sm" className="mr-2" /> : <FaSave className="mr-2" />}
              Save Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings; 